import { Alert, AlertDescription, Button, Input, Label, Popover, PopoverContent, PopoverTrigger, Textarea } from "@EspaceParkQC/sdk-react";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface AccessControlDialogProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  buttonProps: { onClick: () => void; disabled?: boolean; loading?: boolean };
  formProps: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onSubmit: ((reason: string) => Promise<void>) | ((cost?: number) => Promise<void>) | (() => Promise<void>);
    isSubmitting: boolean;
  };
  onSubmit: (value?: string | number) => Promise<void>;
  showCostInput?: boolean;
  noReasonInput?: boolean;
  formOnSubmit: (value?: string | number) => Promise<void>;
  /** "primary" is the recommended action of the moment (filled button); "danger" is an outline in red, never the loudest button. */
  variant?: "default" | "danger" | "primary";
  /** The gate the action will run on, shown in the dialog so it can be checked before submitting. */
  gateName?: string;
  warningMessage?: string;
  requireConfirmBeforeSubmit?: boolean;
  requireConfirmBeforeSubmitMessage?: string;
}

export default function AccessControlDialog({
  title,
  description,
  icon,
  buttonProps,
  formProps,
  formOnSubmit,
  showCostInput = false,
  noReasonInput = false,
  variant = "default",
  gateName,
  warningMessage = "",
  requireConfirmBeforeSubmit = false,
  requireConfirmBeforeSubmitMessage = "",
}: AccessControlDialogProps): React.ReactElement {
  const { t } = useTranslation("main");
  const [reason, setReason] = useState("");
  const [cost, setCost] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    async function submit() {
      if (noReasonInput) {
        await formOnSubmit();
      } else if (showCostInput) {
        await formOnSubmit(cost ? parseFloat(cost) : 0);
      } else {
        await formOnSubmit(reason);
      }
    }

    if (requireConfirmBeforeSubmit) {
      if (window.confirm(requireConfirmBeforeSubmitMessage)) await submit();
    } else {
      await submit();
    }

    setReason("");
    setCost("");
    formProps.onClose();
  };

  return (
    <Popover
      open={formProps.isOpen}
      onOpenChange={(open) => {
        if (open) {
          buttonProps.onClick();
          formProps.onOpen();
        } else {
          formProps.onClose();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant={variant === "primary" ? "default" : "outline"} className={`w-full justify-start ${variant === "danger" ? "text-destructive border-destructive/40" : ""}`} disabled={buttonProps.disabled}>
          {icon}
          {title}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          <div className="space-y-1">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              {icon}
              {title}
            </h4>
            <p className="text-xs text-muted-foreground">{description}</p>
            {gateName && <p className="text-sm font-medium">{t("intercom.access-controls.shared-dialog.gate", { gateName })}</p>}
          </div>

          {warningMessage && (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/20">
              <AlertDescription>{warningMessage}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {noReasonInput ? (
              <p className="text-sm text-muted-foreground">{description}</p>
            ) : showCostInput ? (
              <div className="flex flex-col gap-3">
                <Label htmlFor="cost">{t("intercom.access-controls.shared-dialog.cost-field")}</Label>
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={t("intercom.access-controls.shared-dialog.cost-field-placeholder")}
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  disabled={formProps.isSubmitting}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Label htmlFor="reason">{t("intercom.access-controls.shared-dialog.reason-field")}</Label>
                <Textarea
                  id="reason"
                  placeholder={t("intercom.access-controls.shared-dialog.reason-field-placeholder")}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={formProps.isSubmitting}
                  rows={3}
                />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={formProps.onClose} disabled={formProps.isSubmitting}>
                {t("intercom.access-controls.shared-dialog.cancel")}
              </Button>
              <Button type="submit" variant="default" disabled={formProps.isSubmitting}>
                {formProps.isSubmitting && <Loader2 className="size-4 animate-spin" />}
                {t("intercom.access-controls.shared-dialog.submit")}
              </Button>
            </div>
          </form>
        </div>
      </PopoverContent>
    </Popover>
  );
}
